/* eslint-disable no-nested-ternary */

import { useNavigate, Outlet, useParams, useSearchParams } from 'react-router-dom';

import {
  Button,
  Flex,
  HStack,
  Spacer,
  Text,
  VStack,
  useDisclosure,
  Box,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  TabPanels,
  Center,
  Spinner,
} from '@chakra-ui/react';

import GiftingTopUpModal from 'app/components/Gifting/GiftingTopUpModal';
import ScheduledGiftsTable from 'app/components/Gifting/ScheduledGiftsTable';
import AuthenticatedPageWrapper from 'app/components/UI/AuthenticatedPageWrapper';
import {
  AddCircleOutline,
  Profile2UserOutline,
  EmptyState,
  PointsTransaction,
  YellowWarningBold,
} from '@aslanpay/portal-common-lib';
import TopUpHistoryTable from 'app/utils/table/gifting/topUpHistory';
import ProfileSection from 'app/components/UI/ProfileSection';

import useFetchPots from 'app/hooks/useFetchPots';
import { companyPotQueryParams } from 'app/hooks/usePots';
import { useMemo, useEffect, useState } from 'react';
import RewardHistoryTable from 'app/utils/table/gifting/rewardHistory';
import useAuthContext from 'app/hooks/useAuthContext';
import useFetchCompany from 'app/hooks/useFetchCompany';
import useFetchCompanyConfiguration from 'app/hooks/useFetchCompanyConfiguration';
import formatNumber from 'app/utils/formatting';
import { formatCurrency } from 'app/utils/strings';
import useFetchEmployeesPage from 'app/hooks/useFetchEmployeesPage';
import getPointsValue from 'app/utils/points';
import useInfiniteFetchPointsTransactions from 'app/hooks/useInfiniteFetchPointsTransactions';

const LOW_POINTS_AMOUNT = 1000;
const SCHEDULED_GIFTS_TAB_INDEX = 2;

function Gifting() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [searchParams] = useSearchParams();
  // ?tab=scheduled opens the scheduled gifts, e.g. after scheduling one.
  const [tabIndex, setTabIndex] = useState<number>(
    searchParams.get('tab') === 'scheduled' ? SCHEDULED_GIFTS_TAB_INDEX : 0
  );

  const { companyId } = useParams();
  const { user } = useAuthContext();
  const { data: company } = useFetchCompany(companyId || user?.companyId);
  const { data: companyConfiguration } = useFetchCompanyConfiguration(company?.id);
  const canSchedule = companyConfiguration?.modulesConfig?.modules?.GIFTING === 'ENABLED';

  // Without gifting there's no scheduled gifts tab, so a link asking for it opens the first tab instead.
  useEffect(() => {
    if (companyConfiguration && !canSchedule && tabIndex === SCHEDULED_GIFTS_TAB_INDEX) {
      setTabIndex(0);
    }
  }, [companyConfiguration, canSchedule, tabIndex]);

  /* // Store the final adapted transactions
  const [finalTransactions, setFinalTransactions] = useState<RewardHistoryTableData[] | null>(null);
  const [allPointsTransactions, setAllPointsTransactions] = useState<PointsTransaction[] | null>(
    null
  ); */

  const navigate = useNavigate();

  const { data: companyPots, isLoading: isLoadingPots } = useFetchPots(
    companyPotQueryParams(company?.id)
  );

  const { data: employees } = useFetchEmployeesPage({
    companyId: [company?.id || ''],
    page: 0,
    size: 1,
  });

  const employeesTotal = useMemo(() => employees?.total, [employees]);
  const companyPot = useMemo(() => companyPots?.list?.[0], [companyPots]);
  const companyPointsBalance = useMemo(() => companyPot?.balance || 0, [companyPot]);
  // A scheduled gift is paid when it's sent, so it can be set up before the company tops up.
  const canRewardEmployees = employeesTotal !== 0 && (companyPointsBalance > 0 || canSchedule);

  // state attributes for reward history
  const [allRewardHistoryPointsTransactions, setAllRewardHistoryPointsTransactions] = useState<
    PointsTransaction[]
  >([]);
  const [hasFinishedRewardHistory, sethasFinishedRewardHistory] = useState<boolean>(false);

  // Fetch Reward History Transactions
  const {
    data: rewardHistoryData,
    fetchNextPage: fetchNextRewardHistoryPage,
    hasNextPage: hasNextRewardHistoryPage,
    isFetchingNextPage: isFetchingNextRewardsHistoryPage,
    isSuccess: isRewardHistoryFetchSuccess,
  } = useInfiniteFetchPointsTransactions(
    {
      companyIds: company?.id ? [company.id] : [],
      directions: ['CREDIT'],
      reasons: ['EMPLOYEE_GIFT'],
      sortBy: 'createdAt',
      sort: 'DESC',
      size: 1000,
    },
    !!company?.id
  );

  // Keep asking for more Reward History transactions
  useEffect(() => {
    if (!isRewardHistoryFetchSuccess) return;

    // Flatten the list from all pages
    const flatList = rewardHistoryData.pages.flatMap((page) => page.list);
    setAllRewardHistoryPointsTransactions(flatList);

    // Auto-fetch next page if needed
    if (hasNextRewardHistoryPage && !isFetchingNextRewardsHistoryPage) {
      fetchNextRewardHistoryPage();
    }
  }, [
    rewardHistoryData,
    isRewardHistoryFetchSuccess,
    hasNextRewardHistoryPage,
    isFetchingNextRewardsHistoryPage,
    fetchNextRewardHistoryPage,
  ]);

  // Run after all rewarding history fetch is completed
  useEffect(() => {
    if (hasNextRewardHistoryPage === false) {
      sethasFinishedRewardHistory(true);
    }
  }, [hasNextRewardHistoryPage]);

  /**
   *
   *
   * Code for Company Top Ups
   *
   *
   */

  // state attributes for Company Top Up
  const [allCompanyTopUpPointsTransactions, setAllCompanyTopUpPointsTransactions] = useState<
    PointsTransaction[]
  >([]);
  const [hasFinishedCompanyTopUp, setHasFinishedCompanyTopUp] = useState<boolean>(false);

  // Fetch Company Top Up Points Transactions, only fetch when company id and points bag id are available to avoid fetch without specific company
  const {
    data: companyTopUpData,
    fetchNextPage: fetchNextCompanyTopUpPage,
    hasNextPage: hasNextCompanyTopUpPage,
    isFetchingNextPage: isFetchingNextCompanyTopUpPage,
    isSuccess: isCompanyTopUpFetchSuccess,
    refetch: refetchCompanyTopUpData,
  } = useInfiniteFetchPointsTransactions(
    {
      companyIds: company?.id ? [company.id] : [],
      potIds: companyPot?.id ? [companyPot.id] : [],
      directions: ['CREDIT'],
      reasons: ['COMPANY_TOP_UP'],
      size: 1000,
    },
    !!company?.id && !!companyPot?.id
  );

  // Keep asking for more Company Top Up transactions
  useEffect(() => {
    if (!isCompanyTopUpFetchSuccess) return;

    // Flatten the list from all pages
    const flatList = companyTopUpData.pages.flatMap((page) => page.list);
    setAllCompanyTopUpPointsTransactions(flatList);

    // Auto-fetch next page if needed
    if (hasNextCompanyTopUpPage && !isFetchingNextCompanyTopUpPage) {
      fetchNextCompanyTopUpPage();
    }
  }, [
    isCompanyTopUpFetchSuccess,
    companyTopUpData?.pages,
    hasNextCompanyTopUpPage,
    isFetchingNextCompanyTopUpPage,
    fetchNextCompanyTopUpPage,
  ]);

  // Run after all Company Top Up fetch is completed or when a new top up is added (companyTopUpData changes)
  useEffect(() => {
    if (companyTopUpData && hasNextCompanyTopUpPage === false) {
      setHasFinishedCompanyTopUp(true);
    }
  }, [setHasFinishedCompanyTopUp, hasNextCompanyTopUpPage, companyTopUpData]);

  /**
   * after a successful top up, we need to refetch the company top up history to get the updated list
   */
  const handleTopUpSuccess = () => {
    setHasFinishedCompanyTopUp(false);
    setAllCompanyTopUpPointsTransactions([]);
    refetchCompanyTopUpData();
  };

  return (
    <AuthenticatedPageWrapper title="Gifting">
      <Flex align="start" width="100%" gap="20px">
        {isLoadingPots ? (
          <Center width="full" height="full" flexGrow={1}>
            <Spinner color="brand.purple" size="xl" mb="50px" />
          </Center>
        ) : (
          <>
            <VStack flex="1" align="start">
              <ProfileSection
                title="Company Wallet"
                tooltip="This wallet displays the client's current points balance and allows you to top up with new points. Every 1000 points equates to £5 cost to the client. You can top up in increments of 1000 points."
                maxW="416px"
              >
                <Spacer />
                <Box
                  bg="brand.lightGray2"
                  color="black"
                  py="15px"
                  px="20px"
                  minW="100px"
                  textAlign="left"
                  boxShadow="sm"
                  borderRadius="3px"
                  border="1px solid"
                  borderColor="brand.lightGray1"
                >
                  <Flex direction="column" align="start">
                    <HStack width="100%" justifyContent="space-between" mb="10px">
                      <Text textStyle="h6Medium" color="brand.mutedPurple">
                        Points balance
                      </Text>
                      {companyPointsBalance < LOW_POINTS_AMOUNT && (
                        <YellowWarningBold boxSize="32px" />
                      )}
                    </HStack>
                    <Text textStyle="h3Medium" color="brand.darkPurple">
                      {companyPointsBalance === 0 ? 'Ø' : formatNumber(companyPointsBalance)}
                    </Text>
                  </Flex>
                </Box>

                <Box
                  bg="brand.lightGray2"
                  color="black"
                  py="15px"
                  px="20px"
                  minW="376px"
                  textAlign="left"
                  boxShadow="sm"
                  borderRadius="3px"
                  border="1px solid"
                  borderColor="brand.lightGray1"
                >
                  <Flex direction="column" align="left">
                    <Text textStyle="h6Medium" color="brand.mutedPurple" mb="10px">
                      Value
                    </Text>
                    <Text textStyle="h3Medium" color="brand.darkPurple">
                      {formatCurrency(getPointsValue(companyPointsBalance), 'GBP')}
                    </Text>
                  </Flex>
                </Box>

                {companyPointsBalance < LOW_POINTS_AMOUNT && (
                  <Box
                    bg="alert.warning"
                    color="brand.mutedPurple"
                    borderColor="alert.warning"
                    minW="376px"
                    textAlign="center"
                    p={4}
                  >
                    <HStack>
                      <YellowWarningBold />
                      <Text color="brand.mutedPurple" fontSize="14px">
                        {companyPointsBalance === 0
                          ? 'Top up in order to reward employees'
                          : 'Low balance. Top up your wallet.'}
                      </Text>
                    </HStack>
                  </Box>
                )}

                <Button leftIcon={<AddCircleOutline boxSize="20px" />} size="sm" onClick={onOpen}>
                  Top up wallet
                </Button>

                <GiftingTopUpModal
                  isOpen={isOpen}
                  onClose={onClose}
                  onSuccess={handleTopUpSuccess}
                />

                <Button
                  leftIcon={<Profile2UserOutline boxSize="20px" />}
                  size="sm"
                  border="1px solid"
                  borderColor={canRewardEmployees ? 'brand.purple' : 'system.disabled'}
                  bg="white"
                  color={canRewardEmployees ? 'brand.purple' : 'system.disabled'}
                  _hover={{ bg: 'white' }}
                  _active={{ bg: 'white' }}
                  onClick={() => {
                    if (company?.id) {
                      navigate(`/company-selection/${company.id}/reward-employees`);
                    }
                  }}
                  isDisabled={!canRewardEmployees}
                >
                  Reward Employee(s)
                </Button>

                <Spacer />
              </ProfileSection>
            </VStack>

            <VStack flex="2" spacing={4} align="start">
              <Box
                p="20px"
                borderRadius="9px"
                border="1px solid"
                borderColor="brand.lightGray1"
                maxW="720px"
              >
                <Tabs
                  index={tabIndex}
                  onChange={(index) => setTabIndex(index)}
                  mt="10px"
                  variant="enclosed"
                  defaultValue="tab-1"
                  minW="680px"
                >
                  <TabList>
                    <Tab value="tab-1" title="Top up history" padding="4">
                      Top up history
                    </Tab>
                    <Tab value="tab-2" title="Rewarding history" padding="4">
                      Rewarding history
                    </Tab>
                    {canSchedule && (
                      <Tab
                        value="tab-3"
                        title="Scheduled gifts"
                        padding="4"
                        data-testid="scheduled-gifts-tab"
                      >
                        Scheduled gifts
                      </Tab>
                    )}
                  </TabList>
                  <TabPanels>
                    <TabPanel>
                      {!hasFinishedCompanyTopUp ? (
                        <Spinner />
                      ) : allCompanyTopUpPointsTransactions.length === 0 ? (
                        <EmptyState full text="There is no information to display" />
                      ) : (
                        <TopUpHistoryTable
                          data={allCompanyTopUpPointsTransactions}
                          index={tabIndex}
                        />
                      )}
                    </TabPanel>
                    <TabPanel>
                      {!hasFinishedRewardHistory ? (
                        <Spinner />
                      ) : allRewardHistoryPointsTransactions.length === 0 ? (
                        <EmptyState full text="There is no rewarding history to display" />
                      ) : (
                        <RewardHistoryTable
                          index={tabIndex}
                          transactions={allRewardHistoryPointsTransactions}
                        />
                      )}
                    </TabPanel>
                    {canSchedule && (
                      <TabPanel>
                        <ScheduledGiftsTable />
                      </TabPanel>
                    )}
                  </TabPanels>
                </Tabs>
              </Box>
            </VStack>
          </>
        )}
        <Outlet />
      </Flex>
    </AuthenticatedPageWrapper>
  );
}

export default Gifting;
